import React, { useState, useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { themes, scrollTop, tableRowSx } from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { rowEndStyle, tooltipStyleProps } from "services/constants/Styles";
import sessionHandling from "services/utils/notificationUtils";
import { Loader } from "components/Loader";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { incentiveHistoryHeader } from "../Headers";

export const History = ({ startDate, endDate }) => {
  const { domain, token, userDetails, globalSearchValue, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);
  const [expandedRow, setExpandedRow] = useState(null);
  const { departmentId } = useSelector((state) => state.userReducer);
  const [incentiveDetails, setIncentiveDetails] = useState([1]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [listViewPage, setListViewPage] = useState(0);
  const [loader, setLoader] = useState(true);
  const [approveIncentive, setApproveIncentive] = useState([]);
  const containerRef = useRef(null);

  const handleChangePage = (event, newPage) => {
    setListViewPage(newPage);
    setExpandedRow(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setListViewPage(0);
  };

  useEffect(() => {
    const fetchHistory = async () => {
      setLoader(true);
      try {
        const response = await fetch(
          `${domain}incentive-applications/?start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}&user_id=${userDetails?.id}&status=History`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setApproveIncentive(res);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
      setLoader(false);
    };
    fetchHistory();
  }, [
    departmentId,
    domain,
    endDate,
    startDate,
    token,
    topbarCompanyId,
    userDetails.id,
  ]);

  useEffect(() => {
    const searchFields = [
      "incentive_name",
      "incentive_type",
      "calculation_mode",
      "amount",
      "eligibility_met_employees",
      "status",
    ];
    const filteredResults = approveIncentive.filter((item) =>
      searchFields.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setIncentiveDetails(filteredResults);
    setListViewPage(0);
  }, [approveIncentive, globalSearchValue]);

  useEffect(() => {
    scrollTop(containerRef);
  }, [listViewPage]);

  const handleRowClick = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowId);
    }
  };

  return (
    <React.Fragment>
      {loader ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Grid container sx={{ paddingTop: 2 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{ width: "100%", maxHeight: 730, ml: 2, mr: 2, mt: 2 }}
          >
            <Mui.Table className="border">
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  <Mui.TableCell></Mui.TableCell>
                  {incentiveHistoryHeader.map((header) => (
                    <Mui.TableCell
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        textAlign:
                          header.name === "Eligibility Met Person"
                            ? "center"
                            : "left",
                      }}
                    >
                      {header.name}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {incentiveDetails?.length === 0 && !loader ? (
                  <InfoMessage dataLength={approveIncentive} />
                ) : (
                  incentiveDetails
                    ?.slice(
                      listViewPage * rowsPerPage,
                      listViewPage * rowsPerPage + rowsPerPage
                    )
                    .map((incentive_data) => (
                      <>
                        <Mui.TableRow sx={tableRowSx}>
                          <Mui.TableCell sx={{ width: "1%" }}>
                            {expandedRow === incentive_data.id ? (
                              <Mui.IconButton
                                onClick={() => {
                                  setExpandedRow(null);
                                }}
                              >
                                <KeyboardArrowDown
                                  style={{
                                    color: "black",
                                    fontWeight: "bold",
                                  }}
                                />
                              </Mui.IconButton>
                            ) : (
                              <Mui.IconButton
                                onClick={() =>
                                  handleRowClick(incentive_data.id)
                                }
                              >
                                <KeyboardArrowRight
                                  style={{
                                    color: "black",
                                    fontWeight: "bold",
                                  }}
                                />
                              </Mui.IconButton>
                            )}
                          </Mui.TableCell>
                          {incentiveHistoryHeader.map((header) => (
                            <Mui.TableCell
                              sx={{
                                color: themes.headLine,
                                textAlign:
                                  header.name === "Eligibility Met Person"
                                    ? "center"
                                    : "left",
                              }}
                            >
                              {header?.name === "Incentive Name" ? (
                                incentive_data[header.key]?.length > 30 ? (
                                  <Mui.Tooltip
                                    title={incentive_data[header.key]}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <span>
                                      {incentive_data[header.key].substr(
                                        0,
                                        30
                                      ) + "..."}
                                    </span>
                                  </Mui.Tooltip>
                                ) : (
                                  incentive_data[header.key] || "N/A"
                                )
                              ) : (
                                incentive_data[header.key] || "N/A"
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                        {expandedRow === incentive_data.id && (
                          <Mui.TableRow>
                            <Mui.TableCell colSpan={10}>
                              <Mui.TableContainer
                                sx={{
                                  marginTop: 0.5,
                                  marginLeft: "auto",
                                  marginRight: "auto",
                                  width: "100%px",
                                }}
                                className="w-96 border rounded"
                              >
                                <Mui.Table className="border">
                                  <Mui.Grid
                                    container
                                    sx={{ paddingTop: 1, paddingLeft: 2 }}
                                  >
                                    <Mui.Stack direction="row">
                                      <Mui.Typography
                                        sx={{
                                          fontWeight: "bold",
                                        }}
                                      >
                                        Applied by
                                      </Mui.Typography>
                                      <Mui.Typography sx={{ paddingLeft: 1 }}>
                                        {incentive_data?.appliedby
                                          ? incentive_data?.appliedby
                                          : "-"}
                                      </Mui.Typography>
                                      <Mui.Typography
                                        sx={{
                                          fontWeight: "bold",
                                          paddingLeft: 1,
                                        }}
                                      >
                                        On
                                      </Mui.Typography>
                                      <Mui.Typography sx={{ paddingLeft: 1 }}>
                                        {incentive_data?.appliedat
                                          ? incentive_data?.appliedat
                                          : "-"}
                                      </Mui.Typography>
                                    </Mui.Stack>
                                  </Mui.Grid>
                                  {incentive_data?.status === "Approved" ? (
                                    <Mui.Grid
                                      container
                                      sx={{ paddingTop: 1, paddingLeft: 2 }}
                                    >
                                      <Mui.Stack direction="row">
                                        <Mui.Typography
                                          sx={{
                                            fontWeight: "bold",
                                          }}
                                        >
                                          Approved By
                                        </Mui.Typography>
                                        <Mui.Typography sx={{ paddingLeft: 1 }}>
                                          {incentive_data?.approvedby
                                            ? incentive_data?.approvedby
                                            : "-"}
                                        </Mui.Typography>
                                        <Mui.Typography
                                          sx={{
                                            fontWeight: "bold",
                                            paddingLeft: 1,
                                          }}
                                        >
                                          On
                                        </Mui.Typography>
                                        <Mui.Typography sx={{ paddingLeft: 1 }}>
                                          {incentive_data?.approvedat
                                            ? incentive_data?.approvedat
                                            : "-"}
                                        </Mui.Typography>
                                      </Mui.Stack>
                                    </Mui.Grid>
                                  ) : null}
                                  {incentive_data?.status === "Rejected" ? (
                                    <Mui.Grid
                                      container
                                      sx={{ paddingTop: 1, paddingLeft: 2 }}
                                    >
                                      <Mui.Stack direction="row">
                                        <Mui.Typography
                                          sx={{
                                            fontWeight: "bold",
                                          }}
                                        >
                                          Rejected By
                                        </Mui.Typography>
                                        <Mui.Typography sx={{ paddingLeft: 1 }}>
                                          {incentive_data?.rejectedby
                                            ? incentive_data?.rejectedby
                                            : "-"}
                                        </Mui.Typography>
                                        <Mui.Typography
                                          sx={{
                                            fontWeight: "bold",
                                            paddingLeft: 1,
                                          }}
                                        >
                                          On
                                        </Mui.Typography>
                                        <Mui.Typography sx={{ paddingLeft: 1 }}>
                                          {incentive_data?.rejectedat
                                            ? incentive_data?.rejectedat
                                            : "-"}
                                        </Mui.Typography>
                                      </Mui.Stack>
                                    </Mui.Grid>
                                  ) : null}

                                  <Mui.Stack
                                    direction="row"
                                    sx={{ paddingTop: 0.5 }}
                                  >
                                    <Mui.Typography
                                      sx={{
                                        paddingLeft: 2,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Description :
                                    </Mui.Typography>
                                    <Mui.Typography
                                      sx={{
                                        paddingLeft: 1,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        flex: "1 1 0",
                                      }}
                                    >
                                      {incentive_data.description ?? "N/A"}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                  {incentive_data?.status === "Rejected" && (
                                    <Mui.Stack
                                      direction="row"
                                      sx={{ paddingTop: 0.5 }}
                                    >
                                      <Mui.Typography
                                        sx={{
                                          paddingLeft: 2,
                                          fontWeight: "bold",
                                        }}
                                      >
                                        Reason :
                                      </Mui.Typography>
                                      <Mui.Typography
                                        sx={{
                                          paddingLeft: 1,
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                          flex: "1 1 0",
                                        }}
                                      >
                                        {incentive_data.reason ?? "N/A"}
                                      </Mui.Typography>
                                    </Mui.Stack>
                                  )}
                                </Mui.Table>
                              </Mui.TableContainer>
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
          <Mui.Grid container sx={{ ...rowEndStyle }}>
            {incentiveDetails?.length > 25 && (
              <Mui.TablePagination
                className="custom-pagination"
                component="div"
                rowsPerPageOptions={[25, 50, 75, 100]}
                count={incentiveDetails?.length}
                rowsPerPage={rowsPerPage}
                page={listViewPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Mui.Grid>
        </Mui.Grid>
      )}
    </React.Fragment>
  );
};
